/**
 * Arquivo: src/components/Sidebar.tsx
 * Versão: 6.0.0
 * Data: 12/09/2026
 */
import { useEffect, useState } from 'react'
import {
  BarChart3, ArrowDownCircle, ArrowUpCircle, ArrowLeftRight, CreditCard, ListChecks,
  TrendingUp, Landmark, Tags, Repeat2, WalletCards, Target, FileBarChart, Scale,
  RefreshCw, Settings, UsersRound, History, PanelLeftClose, PanelLeftOpen, CircleUserRound, X,
} from 'lucide-react'
import { NavLink } from 'react-router-dom'

type Item = readonly [string, string, any]
type Grupo = { titulo: string; items: readonly Item[] }

type Props = {
  recolhido: boolean
  onToggle: () => void
  usuario: { nome: string; email: string } | null
  mobileAberto: boolean
  onMobileClose: () => void
}

type UsuarioSidebar = { nome: string; email: string } | null

const grupos: Grupo[] = [
  { titulo: 'Visão geral', items: [['/', 'Dashboard', BarChart3]] },
  { titulo: 'Operação', items: [
    ['/pagar', 'Saídas', ArrowDownCircle], ['/receber', 'Entradas', ArrowUpCircle],
    ['/lancamentos', 'Lançamentos', ListChecks], ['/transferencias', 'Transferências', ArrowLeftRight],
    ['/cartoes', 'Cartões de Crédito', CreditCard], ['/investimentos', 'Investimentos', TrendingUp],
  ] },
  { titulo: 'Planejamento', items: [
    ['/recorrencias', 'Recorrências', Repeat2], ['/orcamentos', 'Orçamentos', WalletCards], ['/metas', 'Metas', Target],
  ] },
  { titulo: 'Cadastros', items: [
    ['/contas', 'Contas e Bancos', Landmark], ['/plano-contas', 'Plano de Contas', Tags],
  ] },
  { titulo: 'Análises', items: [
    ['/relatorios', 'Relatórios', FileBarChart], ['/fiscal', 'Apuração Fiscal', Scale], ['/conciliacao', 'Conciliação', RefreshCw],
  ] },
  { titulo: 'Sistema', items: [
    ['/administracao', 'Administração', UsersRound], ['/auditoria', 'Auditoria', History], ['/configuracoes', 'Configurações', Settings],
  ] },
]

export default function Sidebar({ recolhido, onToggle, usuario, mobileAberto, onMobileClose }: Props) {
  const [usuarioAtual, setUsuarioAtual] = useState<UsuarioSidebar>(usuario)

  useEffect(() => {
    setUsuarioAtual(usuario)
  }, [usuario])

  useEffect(() => {
    if (!mobileAberto) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onMobileClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mobileAberto, onMobileClose])

  const exibido = usuarioAtual || usuario

  return (
    <>
      <button
        className={`sidebar-mobile-overlay${mobileAberto ? ' is-visible' : ''}`}
        type="button"
        aria-label="Fechar menu"
        onClick={onMobileClose}
      />
      <aside className={`sidebar${recolhido ? ' is-collapsed' : ''}${mobileAberto ? ' mobile-open' : ''}`}>
        <div className="brand sidebar-brand-row">
          <div className="brand-copy ff-sidebar-brand">
            <div className="ff-sidebar-brand-art" aria-label="Controle Financeiro">
              <img className="ff-sidebar-symbol" src="/ff-mark.png" alt="" aria-hidden="true" />
              <div className="ff-sidebar-wordmark">CONTROLE FINANCEIRO</div>
              <div className="ff-sidebar-wordmark-line" aria-hidden="true" />
              <div className="ff-sidebar-signature">by cfilho</div>
            </div>
          </div>
          <div className="ff-sidebar-mark-compact" aria-hidden="true">
            <img src="/ff-mark.png" alt="" />
          </div>
          <button className="sidebar-toggle" type="button" onClick={onToggle} title={recolhido ? 'Expandir menu' : 'Recolher menu'}>
            {recolhido ? <PanelLeftOpen size={18}/> : <PanelLeftClose size={18}/>}
          </button>
          <button className="sidebar-mobile-close" type="button" onClick={onMobileClose} title="Fechar menu" aria-label="Fechar menu">
            <X size={20}/>
          </button>
        </div>

        <nav className="nav-list grouped-nav">
          {grupos.map(grupo => (
            <div className="nav-group" key={grupo.titulo}>
              <div className="nav-group-title">{grupo.titulo}</div>
              {grupo.items.map(([to, label, Icon]) => (
                <NavLink
                  key={to}
                  to={to}
                  title={recolhido ? label : undefined}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                  end={to === '/'}
                  onClick={onMobileClose}
                >
                  <Icon size={18} strokeWidth={1.9} /><span>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer sidebar-footer-user">
          <div className="sidebar-user" title={exibido?.email || 'Usuário logado'}>
            <div className="sidebar-user-icon"><CircleUserRound size={20}/></div>
            <div className="sidebar-user-copy">
              <strong>{exibido?.nome || 'Usuário'}</strong>
              <span>{exibido?.email || 'Sessão ativa'}</span>
              <small><i/> Logado</small>
            </div>
          </div>
        </div>
      </aside>
    </>
  )
}
